import { Button, ButtonVariant } from "components/Form/Button";
import { Layout } from "components/Layout";
import { Field } from "components/Form/Field";
import { InputPassword } from "components/Form/InputPassword";
import { memo, useCallback, useState, Dispatch, SetStateAction } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { ReactComponent as Ai } from "assets/ai.svg";
import { apiSetPassword } from "lib/api";
import { toast } from "react-toastify";
import { SignupScreens } from "types";
import { Modal } from "components/Modal";
import { ReactComponent as Success } from "assets/success.svg";
import { Storage, StorageKey } from "lib/storage";
import { AppRoutes } from "types";

type PasswordFormValue = {
  password: string;
  confirmPassword: string;
};

export const PasswordSetup = memo(function PasswordSetup(props: {
  email: string;
  setStep: Dispatch<SetStateAction<SignupScreens>>;
}) {
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showBackWarning, setShowBackWarning] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setError,
    watch,
  } = useForm<PasswordFormValue>({
    mode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  const passwordRequirements = [
    { label: "At least 8 characters", test: (pwd: string) => pwd.length >= 8 },
    {
      label: "One uppercase letter",
      test: (pwd: string) => /[A-Z]/.test(pwd),
    },
    {
      label: "One lowercase letter",
      test: (pwd: string) => /[a-z]/.test(pwd),
    },
    { label: "One number", test: (pwd: string) => /[0-9]/.test(pwd) },
  ];

  const onSubmit = useCallback<SubmitHandler<PasswordFormValue>>(
    async ({ password, confirmPassword }) => {
      // Validate password requirements
      if (
        password.length < 8 ||
        !/[A-Z]/.test(password) ||
        !/[a-z]/.test(password) ||
        !/[0-9]/.test(password)
      ) {
        setError("password", {
          type: "custom",
          message: "Password does not meet requirements",
        });
        return;
      }

      if (password !== confirmPassword) {
        setError("confirmPassword", {
          type: "custom",
          message: "Passwords do not match",
        });
        return;
      }

      setProcessing(true);
      try {
        const result = await apiSetPassword(
          props.email,
          password,
          undefined,
          true
        );
        if (!result.token) {
          throw new Error("Password setup did not return a login token");
        }
        Storage.set(StorageKey.TOKEN, result.token);
        setShowSuccessModal(true);
      } catch (err: any) {
        toast.error("Failed to set password. Please try again.");
        setError("password", {
          type: "custom",
          message: "Failed to set password. Please try again.",
        });
      } finally {
        setProcessing(false);
      }
    },
    [setError, props.email]
  );

  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false);
    toast.success("Account created successfully!");
    // Keep the registered signup token and reload into the authenticated app.
    window.location.href = AppRoutes.home;
  }, []);

  const handleConfirmBack = useCallback(() => {
    // Clear token and go back
    Storage.remove(StorageKey.TOKEN);
    setShowBackWarning(false);
    props.setStep(SignupScreens.EmailVerification);
  }, [props]);

  return (
    <>
      <form className="contents" onSubmit={handleSubmit(onSubmit)}>
        <Layout
          className="h-full text-center"
          toolbar={
            <>
              <Button disabled={!isValid || processing} type="submit">
                {processing ? "Creating..." : "Complete Setup"}
              </Button>
              <Button
                variant={ButtonVariant.FLAT}
                onClick={() => setShowBackWarning(true)}
                disabled={processing}
                type="button"
              >
                Back
              </Button>
            </>
          }
        >
          <div className="grid h-full">
            <div className="grid self-start justify-items-center">
              <Ai />

              <h1 className="text-24 leading-1.2 font-semibold mt-4">
                Set up your password
              </h1>

              <p className="text-858494 mt-3">
                Create a secure password to protect your account
              </p>
            </div>

            <div className="text-left space-y-6">
              <Field
                label="Create password"
                description={errors.password?.message}
                descriptionClassName={
                  errors.password?.message ? "text-fd380f" : undefined
                }
                as="label"
              >
                <InputPassword
                  disabled={processing}
                  id="password"
                  autoCapitalize="off"
                  autoComplete="new-password"
                  autoFocus
                  {...register("password", {
                    required: true,
                    minLength: 8,
                    validate: {
                      hasUpperCase: (value) =>
                        /[A-Z]/.test(value) || "Must contain uppercase letter",
                      hasLowerCase: (value) =>
                        /[a-z]/.test(value) || "Must contain lowercase letter",
                      hasNumber: (value) =>
                        /[0-9]/.test(value) || "Must contain number",
                    },
                  })}
                />
              </Field>

              <Field
                label="Confirm password"
                description={errors.confirmPassword?.message}
                descriptionClassName={
                  errors.confirmPassword?.message ? "text-fd380f" : undefined
                }
                as="label"
              >
                <InputPassword
                  disabled={processing}
                  id="confirmPassword"
                  autoCapitalize="off"
                  autoComplete="new-password"
                  {...register("confirmPassword", {
                    required: true,
                    validate: (value) =>
                      value === password || "Passwords do not match",
                  })}
                />
              </Field>

              <div className="space-y-3 p-4 bg-f4f7ff rounded-lg">
                <p className="text-14 font-semibold text-454545">
                  Password requirements:
                </p>
                <ul className="space-y-2">
                  {passwordRequirements.map((req, index) => {
                    const isValid = password ? req.test(password) : false;
                    return (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-14"
                      >
                        <span
                          className={`w-4 h-4 rounded-full flex items-center justify-center ${
                            isValid
                              ? "bg-green-500 text-white"
                              : "bg-d1d5db text-858494"
                          }`}
                        >
                          {isValid ? "✓" : "○"}
                        </span>
                        <span
                          className={isValid ? "text-green-700" : "text-858494"}
                        >
                          {req.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="space-y-3 items-center">
                <p className="text-14 font-semibold text-858494 text-decoration-line: underline">
                  IMPORTANT NOTE
                </p>
                <p className="text-14 text-858494">
                  This password is for accessing your ContributorID account. It
                  is <strong>NOT the campaign passphrase</strong> you used
                  during signup.
                </p>
              </div>
            </div>
          </div>
        </Layout>
      </form>

      <Modal show={showSuccessModal} onClose={handleSuccessModalClose}>
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <Success />
          </div>

          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Account Created!</h2>
            <p className="text-858494">
              Your ContributorID has been successfully created. You are now
              logged in and can continue to the available tasks.
            </p>
          </div>

          <Button onClick={handleSuccessModalClose} className="w-full">
            Continue
          </Button>
        </div>
      </Modal>

      <Modal show={showBackWarning} onClose={() => setShowBackWarning(false)}>
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Exit setup?</h2>
            <p className="text-858494">
              You have already registered. If you exit now, you'll need to setup
              a password from the login screen.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setShowBackWarning(false)}
              className="flex-1"
            >
              Stay Here
            </Button>
            <Button
              onClick={handleConfirmBack}
              variant={ButtonVariant.FLAT_BORDER}
              className="flex-1"
            >
              Exit
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
});
